import type { ReactTable, TableState } from '@tanstack/react-table';
import { useImperativeHandle, useRef, type Ref, type RefObject } from 'react';

import { useMergeRefs } from '../../../core/hooks/useMergeRefs.js';
import { useDataTableConfigElement } from '../../hooks/useDataTableConfig.js';
import type { DataTableColumnSettingsModalRef } from '../components/Toolbar/ColumnSettings/column-settings-types.js';
import { DATA_TABLE_DETAILS_ROW_IDENTIFIER } from '../constants.js';
import type {
  DataTableColumnSettingsConfig,
  DataTableRef,
} from '../public.api.js';
import type { DataTableRowData } from '../row-data-types.js';
import type {
  DataTableRowThreshold,
  DataTableThresholdProps,
} from './Thresholds/threshold-types.js';
import type { TDataTableFeatures } from './types/features.types.js';

/**
 * Extends the ref of the DataTable with additional configuration and control options.
 * @internal
 */
export function useDataTableConfigRef<TData extends DataTableRowData>(
  forwardedRef: Ref<DataTableRef>,
  columnSettingsModalRef: RefObject<DataTableColumnSettingsModalRef | null>,
  table: ReactTable<TDataTableFeatures, Exclude<TData, unknown>>,
  props: DataTableThresholdProps<TData>,
) {
  /*
   * Collect the refs and create an imperative handle in order to expose the
   * functions we need on the DataTable.
   */
  const containerRef = useRef<HTMLDivElement>(null);
  const rootRef = useMergeRefs<DataTableRef | HTMLDivElement>([
    forwardedRef,
    containerRef,
  ]);

  const configRowThresholds = useDataTableConfigElement('rowThresholds');

  useImperativeHandle(forwardedRef, () => ({
    element: containerRef.current,
    // eslint-disable-next-line @typescript-eslint/ban-ts-comment
    // @ts-ignore

    getConfig() {
      return {
        pagination: {
          defaultPageIndex: (
            table.store.state as unknown as TableState<TDataTableFeatures>
          ).pagination?.pageIndex,
          defaultPageSize: (
            table.store.state as unknown as TableState<TDataTableFeatures>
          ).pagination?.pageSize,
        },
        columnVisibility: (
          table.store.state as unknown as TableState<TDataTableFeatures>
        ).columnVisibility,
        fullWidth: table.optionsStore.state.layout?.fullWidth,
        fullHeight: table.optionsStore.state.layout?.fullHeight,
        lineWrap: (
          table.store.state as unknown as TableState<TDataTableFeatures>
        ).columnLineWrap,
        resizable: table.optionsStore.state.enableColumnResizing,
        selectableRows: table.optionsStore.state.enableRowSelection,
        // NOTE: typecast necessary due to generic resolving to unknown
        rowThresholds: (props.rowThresholds ?? configRowThresholds) as
          | DataTableRowThreshold<TData>[]
          | undefined,
        sortable: table.optionsStore.state.enableSorting,
        variant: (
          table.store.state as unknown as TableState<TDataTableFeatures>
        ).visualVariantConfig,
      };
    },
    downloadData(
      subset: 'all' | 'page' | 'selected',
      excludeColumns?: string[],
    ) {
      switch (subset) {
        case 'all':
          table.downloadAllData(excludeColumns);
          break;
        case 'page':
          table.downloadPageData(excludeColumns);
          break;
        case 'selected':
          table.downloadSelectedData(excludeColumns);
          break;
      }
    },
    scrollToRow: table.scrollToRow,
    getDisplayedRowIds(): string[] {
      const result: string[] = [];
      for (const row of table.getRowModel().rows) {
        if (!row[DATA_TABLE_DETAILS_ROW_IDENTIFIER]) {
          result.push(row.id);
        }
      }
      return result;
    },
    openColumnSettings: (columnSettings?: DataTableColumnSettingsConfig) => {
      columnSettingsModalRef.current?.openColumnSettings(columnSettings);
    },
  }));

  return rootRef;
}
